import { useState, useRef, useEffect } from 'react';
import { useSettings } from '../../../../contexts/SettingsContext';
import { getLocationName, searchLocations } from '../../../../services/locationService';
import { useLocation } from '../../../../contexts/LocationContext';

// Helper to extract main place name from suggestion
function extractPlaceName(suggestion) {
  if (!suggestion) return '';
  // Try to get city/town/village/suburb from address if available
  if (suggestion.address) {
    return (
      suggestion.address.city ||
      suggestion.address.town ||
      suggestion.address.village ||
      suggestion.address.suburb ||
      (suggestion.display_name ? suggestion.display_name.split(',')[0].trim() : '')
    );
  }
  // Fallback: first part of display_name
  if (suggestion.display_name) {
    return suggestion.display_name.split(',')[0].trim();
  }
  return '';
}

export const useLocationSettings = () => {
  const { settings, updateSettings } = useSettings();
  const { getLocation } = useLocation();
  const [location, setLocation] = useState(settings.location || '');
  const [isLoading, setIsLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);

  const handleLocationChange = (event, { newValue }) => {
    setLocation(newValue);
  };

  const handleDeleteLocation = () => {
    updateSettings({ location: null, coordinates: null });
    setLocation('');
  };

  const handleAutoDetectLocation = async () => {
    setIsLoading(true);
    try {
      const coords = await getLocation();
      const { latitude, longitude } = coords;
      
      try {
        const locationName = await getLocationName(latitude, longitude);
        
        const locationUpdate = {
          location: locationName,
          coordinates: {
            lat: latitude,
            lon: longitude
          }
        };
        
        setLocation(locationName);
        await updateSettings(locationUpdate);
      } catch (error) {
        console.error("Error getting location name:", error);
      }
    } catch (error) {
      console.error("Error detecting location:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const debounceTimeoutRef = useRef(null);

  const onSuggestionsFetchRequested = ({ value }) => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    debounceTimeoutRef.current = setTimeout(async () => {
      try {
        const results = await searchLocations(value);
        setSuggestions(results);
      } catch (error) {
        console.error('Error fetching suggestions:', error);
        setSuggestions([]);
      }
    }, 300);
  };

  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);
    };
  }, []);

  const onSuggestionsClearRequested = () => {
    setSuggestions([]);
  };

  const onSuggestionSelected = async (event, { suggestion }) => {
    const placeName = extractPlaceName(suggestion);
    setLocation(placeName);
    await updateSettings({
      ...settings,
      location: placeName,
      coordinates: {
        lat: suggestion.lat,
        lon: suggestion.lon
      }
    });
  };

  const getSuggestionValue = suggestion => extractPlaceName(suggestion);

  return {
    location,
    suggestions,
    isLoading,
    settings,
    handleLocationChange,
    handleAutoDetectLocation,
    handleDeleteLocation,
    onSuggestionsFetchRequested,
    onSuggestionsClearRequested,
    onSuggestionSelected,
    getSuggestionValue,
  };
}; 